import _ from 'lodash';

export function modelState<T>(initialValue: T, equalityComparer: (((x: T, y: T) => boolean) | null) = null) {
    let lastValue = initialValue;
    let nextValue = initialValue;
    let isDirty = false;
    const subscriptions = [] as ((prevValue: T) => void)[];

    function notify(prevValue: T) {
        subscriptions.forEach(subscription => subscription(prevValue));
    }

    function isEqual(x: T,y: T) {
        if (equalityComparer) {
            return equalityComparer(x, y);
        }

        return _.isEqual(x, y);
    }

    return {
        setValue(value: T) {
            if (isEqual(value, nextValue)) {
                return;
            }

            const prevValue = nextValue;

            nextValue = value;
            isDirty = !isEqual(lastValue, nextValue);
            notify(prevValue);
        },
        commit() {
            if (!isDirty) {
                return;
            }

            const prevValue = nextValue;

            lastValue = nextValue;
            isDirty = false;
            notify(prevValue);
        },
        rollback() {
            if (!isDirty) {
                return;
            }

            const prevValue = nextValue;

            nextValue = lastValue;
            isDirty = false;
            notify(prevValue);
        },
        get oldValue() {
            return lastValue;
        },
        get value() {
            return nextValue;
        },
        get isDirty() {
            return isDirty;
        },
        subscribe(callback: (prevValue: T) => void): () => void {
            subscriptions.push(callback);

            return () => this.unsubscribe(callback);
        },
        unsubscribe(callback: (prevValue: T) => void) {
            const index = subscriptions.indexOf(callback);
            if (index >= 0) {
                subscriptions.splice(index, 1);
            }
        },
    }
}

export interface modelStateBase {
    subscribe(callback: () => void): () => void;
    unsubscribe(callback: () => void): void;
};

export interface modelStateContainer {
    IsDirty: modelState<boolean>;
}

export type modelState<T> = ReturnType<typeof modelState<T>> & modelStateBase;

export type CollectionChangedEventArgs<T> = {
    action: 'add' | 'remove' | 'reset';
    item?: T;
    index?: number;
};

export class ModelStateList<T> {
    private _initial: T[] = [];
    private _list: T[] = [];
    private _changing: boolean = false;

    public isDirty: modelState<boolean> = modelState(false);

    private collectionChangedCallbacks: ((args: CollectionChangedEventArgs<T>) => void)[] = [];

    private _additions: T[] = [];
    private _removals: T[] = [];

    private _equalityComparer: ((x: T, y: T) => boolean) | null = null;

    constructor(initial?: Iterable<T>, equalityComparer?: ((x: T, y: T) => boolean)) {
        if (initial) {
            this._initial = Array.from(initial);
            this._list = Array.from(initial);
        }

        this._equalityComparer = equalityComparer;
    }

    get count(): number {
        return this._list.length;
    }

    get changing(): boolean {
        return this._changing;
    }

    [Symbol.iterator](): Iterator<T> {
        return this._list.values();
    }

    toArray(): T[] {
        return Array.from(this._list);
    }

    additionsToArray(): T[] {
        return Array.from(this._additions);
    }

    removalsToArray(): T[] {
        return Array.from(this._removals);
    }

    add(item: T): void {
        this._list.push(item);
        this._additions = this._list.filter(i => !this._initial.includes(i));
        this._removals = this._initial.filter(i => !this._list.includes(i));
        this.updateIsDirty();
        this.triggerCollectionChanged({ action: 'add', item });
    }

    insert(index: number, item: T): void {
        this._list.splice(index, 0, item);
        this._additions = this._list.filter(i => !this._initial.includes(i));
        this._removals = this._initial.filter(i => !this._list.includes(i));
        this.updateIsDirty();
        this.triggerCollectionChanged({ action: 'add', item, index });
    }

    insertAfter(after: T, item: T): void {
        const index = this._list.indexOf(after);
        if (index !== -1) {
            this.insert(index + 1, item);
        }
    }

    remove(item: T): void {

        const additionIndex = this._additions.indexOf(item);
        if (additionIndex !== -1) {
            this._additions = this._additions.filter(i => i !== item);
        } else {
            this._removals = [...this._removals, item];
        }

        const index = this._list.indexOf(item);
        if (index !== -1) {
            this._list.splice(index, 1);
            this.updateIsDirty();
            this.triggerCollectionChanged({ action: 'remove', item, index });
        }
    }

    removeAt(index: number): void {
        this.remove(this._list[index]);
    }

    clear(): void {
        this._additions = [];
        this._removals = [...this._initial];

        this._list = [];
        this.updateIsDirty();
        this.triggerCollectionChanged({ action: 'reset' });
    }

    //replaceAll(items: T[]): void {
    //    // Clear everything and replace with incoming items and then notify of reset
    //    this._additions = [];
    //    this._removals = [];
    //    this._list = [...items];

    //    this.isDirty.setValue(false);
    //    this.triggerCollectionChanged({ action: 'reset' });
    //}

    replace(item: T, newItem: T): void {
        const index = this._list.indexOf(item);
        if (index !== -1) {
            this.remove(item);
            this.insert(index, newItem);
        }
    }

    beginChange(): void {
        this._changing = true;
    }

    cancelChange(): void {
        this._changing = false;
    }

    commitChange(): void {
        this._additions = [];
        this._removals = [];

        this._changing = false;
        this._initial = [...this._list];
        this.isDirty.setValue(false);
    }

    undo(): void {
        this._additions = [];
        this._removals = [];

        this._list = [...this._initial];
        this.isDirty.setValue(false);
        this.triggerCollectionChanged({ action: 'reset' });
    }

    // Event handling
    subscribe(callback: (args: CollectionChangedEventArgs<T>) => void): () => void {
        this.collectionChangedCallbacks.push(callback);

        return () => this.unsubscribe(callback);
    }

    unsubscribe(callback: (args: CollectionChangedEventArgs<T>) => void): void {
        const index = this.collectionChangedCallbacks.indexOf(callback);
        if (index !== -1) {
            this.collectionChangedCallbacks.splice(index, 1);
        }
    }

    private triggerCollectionChanged(args: CollectionChangedEventArgs<T>): void {
        for (const callback of this.collectionChangedCallbacks) {
            callback(args);
        }
    }

    private updateIsDirty(): void {
        this.isDirty.setValue(!this.areArraysEqual(this._list, this._initial));
    }

    private areArraysEqual(arr1: T[], arr2: T[]): boolean {
        if (arr1.length !== arr2.length) {
            return false;
        }
        for (let i = 0; i < arr1.length; i++) {
            if (!this.isEqual(arr1[i], arr2[i])) {
                return false;
            }
        }
        return true;
    }

    private isEqual(x: T, y: T) {
        if (this._equalityComparer) {
            return this._equalityComparer(x, y);
        }

        return _.isEqual(x, y);
    }
}

export function modelStateListSubscriptionManager<T>(
    list: ModelStateList<T>,
    subscriptionFactory: (item: T) => () => void) {
    const items = list.toArray();

    const subscriptions = new Map<T, () => void>();

    items.forEach(i => {
        const subscription = subscriptionFactory(i);
        subscriptions.set(i, subscription);
    });

    const unsubscribeList = list.subscribe((args) => {
        if (args.action === 'add') {
            const subscription = subscriptionFactory(args.item);
            subscriptions.set(args.item, subscription);
        } else if (args.action === 'remove') {
            const subscription = subscriptions.get(args.item);
            if (subscription) {
                subscription();
                subscriptions.delete(args.item);
            }
        } else if (args.action === 'reset') {
            subscriptions.forEach(subscription => subscription());
            subscriptions.clear();

            list.toArray().forEach(item => {
                const subscription = subscriptionFactory(item);
                subscriptions.set(item, subscription);
            });
        }
    });

    return () => {
        unsubscribeList();

        subscriptions.forEach(unsubscribe => unsubscribe());
    };
}



export function setupIsDirtySubscriptionsV2<T extends modelStateContainer>(
    obj: T,
    observables: modelStateBase[],
    dependencies: ((obj: T) => boolean)[],
    children: (() => void)[] = []) {

    const subscriptions = observables.map((observable, index) => {
        return observable.subscribe(() => {
            obj.IsDirty.setValue(dependencies.some(d => d(obj)));
        });
    });

    children.forEach(child => {
        child();
    });

    return () => {
        subscriptions.forEach(unsubscribe => unsubscribe());
    };
}

export function buildIsDirtyChild<T>(
    list: ModelStateList<T>,
    parentIsDirty: modelState<boolean>,
    childDirtyAccessor: (item: T) => modelState<boolean>,
    setup: (item: T) => () => void) {

    return () => modelStateListSubscriptionManager(list, item => {
        const subscriptions: (() => void)[] = [];

        subscriptions.push(setup(item));

        subscriptions.push(childDirtyAccessor(item).subscribe(() => {
            parentIsDirty.setValue(list.toArray().some(item => childDirtyAccessor(item).value));
        }));

        return () => {
            subscriptions.forEach(unsubscribe => unsubscribe());
        };
    });
}

export function bulkSubscribe<T>(list: ModelStateList<T>, accessors: ((item: T) => modelStateBase)[], callback: () => void) {
    const unsubscribeList = list.subscribe(callback);

    const unsubscribeManager = modelStateListSubscriptionManager(list, item => {
        const subscriptions = accessors.map(accessor => accessor(item).subscribe(callback));

        return () => {
            subscriptions.forEach(unsubscribe => unsubscribe());
        };
    });

    return () => {
        unsubscribeList();
        unsubscribeManager();
    };
}

interface bulkSubscribeContextContainer<T> {
    context: T;
    dispose: () => void;
}

export function bulkSubscribeWithContext<T, U>(
    list: ModelStateList<T>,
    contextFactory: (item: T) => bulkSubscribeContextContainer<U>,
    accessors: ((item: T) => modelStateBase)[],
    callback: (context: U) => void) {

    return modelStateListSubscriptionManager(list, item => {
        const contextContainer = contextFactory(item);

        const subscriptions = accessors.map(accessor => {
            return accessor(item).subscribe(() => {
                callback(contextContainer.context);
            });
        });

        return () => {
            subscriptions.forEach(unsubscribe => unsubscribe());
            contextContainer.dispose();
        };
    });
}

export function bulkSubscribeSingle<T, U>(
    list: ModelStateList<T>,
    accessor: ((item: T) => modelState<U>),
    callback: ((item: T, prevVal: U) => void)) {

    return modelStateListSubscriptionManager(list, item => {
        return accessor(item).subscribe((prevValue) => {
            callback(item, prevValue);
        });
    });
}

export function feedValueDown<T>(parent: modelState<T>, children: ((val: T) => void)[]) {
    return parent.subscribe(() => {
        children.forEach(child => {
            child(parent.value);
        });
    });
}

export function buildFeedDownChild<T, U>(
    list: ModelStateList<T>,
    accessor: (item: T) => modelState<U>,
    transform?: (existing: U, incoming: U) => U) {

    return (val: U) => {
        list.toArray().forEach(item => {
            const child = accessor(item);
            child.setValue(transform ? transform(child.value, val) : val);
        });
    };
}